// Central API client for the frontend.
//
// Goals (Sprint 4 / D1):
//  - one place that prepends API_BASE and always sends credentials
//  - JSON-encode plain-object bodies + set Content-Type automatically
//  - consistent error handling: throw a typed ApiError instead of returning !res.ok
//  - a single global signal on 401 so auth state + routing react in one place
//  - opt-in retry with backoff for idempotent requests (network / 5xx)
//  - a central client-side error logging hook
//
// Usage:
//   import { api, ApiError, errorText } from '../lib/api';
//   const data = await api.get<{ jobs: Job[] }>('/api/jobs');
//   try { await api.del(`/api/resumes/${id}`); }
//   catch (e) { toast.error(errorText(e, 'Delete failed')); }

import { API_BASE } from '../config';

/** Dispatched on the window whenever any request returns HTTP 401. */
export const UNAUTHORIZED_EVENT = 'auth:unauthorized';

export class ApiError extends Error {
  readonly status: number;
  readonly data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  /** Plain objects are JSON-encoded; strings/FormData are sent as-is. */
  body?: unknown;
  /** Skip JSON-encoding even for objects (caller supplies a valid BodyInit). */
  rawBody?: boolean;
  /** Retries for idempotent (GET/HEAD) requests on network/5xx errors. Default 0. */
  retry?: number;
}

function isFormData(value: unknown): value is FormData {
  return typeof FormData !== 'undefined' && value instanceof FormData;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function backoff(attempt: number): number {
  const base = Math.min(500 * 2 ** (attempt - 1), 4000);
  return base + Math.floor(Math.random() * 150); // jitter
}

async function readBody(res: Response): Promise<unknown> {
  const contentType = res.headers.get('content-type') || '';
  try {
    if (contentType.includes('application/json')) return await res.json();
    const text = await res.text();
    return text || null;
  } catch {
    return null;
  }
}

function errorMessage(data: unknown, status: number): string {
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (typeof obj.error === 'string' && obj.error) return obj.error;
    if (typeof obj.message === 'string' && obj.message) return obj.message;
  }
  if (typeof data === 'string' && data.trim()) return data;
  if (status === 0) return 'Network error — check your connection and try again.';
  if (status >= 500) return `Server error (${status}). Please try again.`;
  if (status === 401) return 'Your session has expired. Please sign in again.';
  return `Request failed (${status}).`;
}

function logError(scope: string, url: string, detail: unknown): void {
  // Central client-side error logging hook. Swap console for telemetry later.
  // eslint-disable-next-line no-console
  console.error(`[api:${scope}] ${url}`, detail);
}

/**
 * Low-level fetch wrapper. Returns the raw Response (does not throw on !ok),
 * but dispatches UNAUTHORIZED_EVENT on 401 and retries idempotent requests.
 */
export async function apiFetch(path: string, options: ApiOptions = {}): Promise<Response> {
  const { body, rawBody, retry = 0, headers, method, ...rest } = options;
  const url = /^https?:\/\//.test(path) ? path : `${API_BASE}${path}`;
  const verb = (method || 'GET').toUpperCase();
  const idempotent = verb === 'GET' || verb === 'HEAD';

  const finalHeaders = new Headers(headers);
  let finalBody: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    if (rawBody || isFormData(body) || typeof body === 'string') {
      finalBody = body as BodyInit;
    } else {
      finalBody = JSON.stringify(body);
      if (!finalHeaders.has('Content-Type')) finalHeaders.set('Content-Type', 'application/json');
    }
  }

  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    method: verb,
    headers: finalHeaders,
    body: finalBody,
  };

  let attempt = 0;
  for (;;) {
    let res: Response;
    try {
      res = await fetch(url, init);
    } catch (err) {
      if (idempotent && attempt < retry) {
        attempt += 1;
        await delay(backoff(attempt));
        continue;
      }
      logError('network', url, err);
      throw new ApiError(errorMessage(null, 0), 0, null);
    }

    if (res.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    }

    if (res.status >= 500 && idempotent && attempt < retry) {
      attempt += 1;
      await delay(backoff(attempt));
      continue;
    }

    return res;
  }
}

/** High-level request: resolves to parsed JSON on success, throws ApiError otherwise. */
export async function request<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const res = await apiFetch(path, options);
  const data = await readBody(res);
  if (!res.ok) {
    const err = new ApiError(errorMessage(data, res.status), res.status, data);
    if (res.status >= 500 || res.status === 0) logError('http', path, err);
    throw err;
  }
  return data as T;
}

export const api = {
  get: <T = unknown>(path: string, options?: ApiOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, options?: ApiOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T = unknown>(path: string, body?: unknown, options?: ApiOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T = unknown>(path: string, body?: unknown, options?: ApiOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  del: <T = unknown>(path: string, options?: ApiOptions) => request<T>(path, { ...options, method: 'DELETE' }),
  upload: <T = unknown>(path: string, form: FormData, options?: ApiOptions) =>
    request<T>(path, { ...options, method: 'POST', body: form }),
  raw: apiFetch,
};

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Best-effort human message from any thrown value. */
export function errorText(error: unknown, fallback = 'Something went wrong.'): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
